import type { CalendarItem } from './calendar-item';
import { layerOf, type CalendarLayer } from './calendar-sources';

// Which layers are on, as pure rules: the reader's choice, the provider's tick, the first default a layer was
// given. `use-shown-layers.ts` holds the state and storage around them; the shared calendar contract runs these.

/** The reader's own show/hide per layer; a layer they never touched follows its default. */
export type LayerChoices = Record<string, boolean>;

/**
 * Whether a layer is on. The reader's own choice first; then the provider's "shown" tick, which is the
 * truth whenever it is sent; else the default this layer got the first time it was seen, so one odd
 * answer from a provider can't flip what is shown; else today's guess.
 */
export function isLayerShown(layer: CalendarLayer, choices: LayerChoices, defaults: LayerChoices): boolean {
  return choices[layer.id] ?? (layer.shownByProvider ? layer.shownByDefault : defaults[layer.id] ?? layer.shownByDefault);
}

/**
 * The defaults to store once every outside source has answered: each layer without a provider tick that has
 * none yet takes today's guess, and a stored one is never overwritten. `undefined` when there is nothing to write.
 */
export function nextLayerDefaults(layers: CalendarLayer[], defaults: LayerChoices, settledLayers: boolean): LayerChoices | undefined {
  const unseen = layers.filter((layer) => !layer.shownByProvider && defaults[layer.id] === undefined);
  if (!settledLayers || unseen.length === 0) return undefined;
  return { ...defaults, ...Object.fromEntries(unseen.map((layer) => [layer.id, layer.shownByDefault])) };
}

/**
 * The items of the layers that are on. An item of a layer not listed yet is kept rather than hidden.
 * A meeting that matches a booking's title and time stays beside it: those prove no shared identity across sources.
 */
export function itemsOfShownLayers(items: CalendarItem[], layers: CalendarLayer[], shown: Set<string>): CalendarItem[] {
  const known = new Set(layers.map((layer) => layer.id));
  return items.filter((item) => !known.has(layerOf(item)) || shown.has(layerOf(item)));
}
